import { booleanPointInPolygon, booleanValid, polygon } from '@turf/turf'
import {
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlate,
  GeckoPolygonalPlate,
} from 'types'

import { getMostRecentPlateInBin } from './repairHelpers'

type SupportedPlate = GeckoPlate | GeckoPolygonalPlate

type Bounds = {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

type PreparedPlate<T extends SupportedPlate = SupportedPlate> = {
  plate: T
  bounds: Bounds
}

type PreparedPolygonalPlate = PreparedPlate<GeckoPolygonalPlate> & {
  polygon: ReturnType<typeof polygon>
}

type BucketIndex<T extends PreparedPlate> = {
  bounds: Bounds
  columns: number
  rows: number
  buckets: Map<number, T[]>
}

export type BasicShipHullManufactureSpecs = {
  nominalThickness: GeckoMeasurement | undefined
  installationDate: Date | undefined
}

export type BasicShipHullNominalResolver = (
  binBounds: GeckoComponentRect,
  latestDateToUse: Date | undefined
) => BasicShipHullManufactureSpecs

const METERS = GeckoMeasurementUnit.Meters

function validateNominalThickness(
  componentId: string,
  featureId: string,
  nominalThickness: GeckoMeasurement
) {
  const nominalInMeters = nominalThickness.toNumber(METERS)
  if (!Number.isFinite(nominalInMeters) || nominalInMeters <= 0) {
    throw new Error(
      `Invalid wall nominal thickness for component ${componentId}, feature ${featureId}`
    )
  }
}

function preparePolygonalPlate(
  componentId: string,
  plate: GeckoPolygonalPlate
): PreparedPolygonalPlate | undefined {
  if (!plate.wallNominalThickness) return undefined

  validateNominalThickness(componentId, plate.id, plate.wallNominalThickness)
  const coordinates = plate.geometry.map(({ x, y }) => [
    x.toNumber(METERS),
    y.toNumber(METERS),
  ])
  if (
    coordinates.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))
  ) {
    throw new Error(
      `Invalid polygon geometry for component ${componentId}, feature ${plate.id}`
    )
  }

  const first = coordinates[0]
  const last = coordinates.at(-1)
  const closedCoordinates =
    first && last && first[0] === last[0] && first[1] === last[1]
      ? coordinates
      : [...coordinates, first]
  const platePolygon = polygon([closedCoordinates])
  if (!booleanValid(platePolygon)) {
    throw new Error(
      `Invalid polygon geometry for component ${componentId}, feature ${plate.id}`
    )
  }

  const xCoordinates = coordinates.map(([x]) => x)
  const yCoordinates = coordinates.map(([, y]) => y)
  return {
    plate,
    polygon: platePolygon,
    bounds: {
      minX: Math.min(...xCoordinates),
      minY: Math.min(...yCoordinates),
      maxX: Math.max(...xCoordinates),
      maxY: Math.max(...yCoordinates),
    },
  }
}

function prepareRectangularPlate(
  componentId: string,
  plate: GeckoPlate
): PreparedPlate<GeckoPlate> | undefined {
  if (!plate.wallNominalThickness) return undefined

  validateNominalThickness(componentId, plate.id, plate.wallNominalThickness)
  const xStart = plate.geometry.x.start.toNumber(METERS)
  const xEnd = plate.geometry.x.end.toNumber(METERS)
  const yStart = plate.geometry.y.start.toNumber(METERS)
  const yEnd = plate.geometry.y.end.toNumber(METERS)
  if (![xStart, xEnd, yStart, yEnd].every(Number.isFinite)) {
    throw new Error(
      `Invalid rectangular geometry for component ${componentId}, feature ${plate.id}`
    )
  }

  return {
    plate,
    bounds: {
      minX: Math.min(xStart, xEnd),
      minY: Math.min(yStart, yEnd),
      maxX: Math.max(xStart, xEnd),
      maxY: Math.max(yStart, yEnd),
    },
  }
}

function getBucketCoordinate(
  value: number,
  start: number,
  end: number,
  count: number
) {
  if (end === start) return 0
  return Math.min(
    count - 1,
    Math.max(0, Math.floor(((value - start) / (end - start)) * count))
  )
}

function createBucketIndex<T extends PreparedPlate>(plates: T[]) {
  if (plates.length === 0) return undefined

  const bounds = plates.reduce<Bounds>(
    (combined, plate) => ({
      minX: Math.min(combined.minX, plate.bounds.minX),
      minY: Math.min(combined.minY, plate.bounds.minY),
      maxX: Math.max(combined.maxX, plate.bounds.maxX),
      maxY: Math.max(combined.maxY, plate.bounds.maxY),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    }
  )
  const gridSize = Math.max(1, Math.ceil(Math.sqrt(plates.length)))
  const index: BucketIndex<T> = {
    bounds,
    columns: gridSize,
    rows: gridSize,
    buckets: new Map(),
  }

  for (const plate of plates) {
    const startColumn = getBucketCoordinate(
      plate.bounds.minX,
      bounds.minX,
      bounds.maxX,
      index.columns
    )
    const endColumn = getBucketCoordinate(
      plate.bounds.maxX,
      bounds.minX,
      bounds.maxX,
      index.columns
    )
    const startRow = getBucketCoordinate(
      plate.bounds.minY,
      bounds.minY,
      bounds.maxY,
      index.rows
    )
    const endRow = getBucketCoordinate(
      plate.bounds.maxY,
      bounds.minY,
      bounds.maxY,
      index.rows
    )

    for (let row = startRow; row <= endRow; row += 1) {
      for (let column = startColumn; column <= endColumn; column += 1) {
        const bucketKey = row * index.columns + column
        const bucket = index.buckets.get(bucketKey)
        if (bucket) bucket.push(plate)
        else index.buckets.set(bucketKey, [plate])
      }
    }
  }

  return index
}

function getCandidates<T extends PreparedPlate>(
  index: BucketIndex<T> | undefined,
  x: number,
  y: number
) {
  if (
    !index ||
    x < index.bounds.minX ||
    x > index.bounds.maxX ||
    y < index.bounds.minY ||
    y > index.bounds.maxY
  ) {
    return []
  }

  const column = getBucketCoordinate(
    x,
    index.bounds.minX,
    index.bounds.maxX,
    index.columns
  )
  const row = getBucketCoordinate(
    y,
    index.bounds.minY,
    index.bounds.maxY,
    index.rows
  )
  return index.buckets.get(row * index.columns + column) ?? []
}

function getBinCenter(binBounds: GeckoComponentRect) {
  const xStart = binBounds.x.start.toNumber(METERS)
  const xEnd = binBounds.x.end.toNumber(METERS)
  const yStart = binBounds.y.start.toNumber(METERS)
  const yEnd = binBounds.y.end.toNumber(METERS)
  return { x: (xStart + xEnd) / 2, y: (yStart + yEnd) / 2 }
}

function resolveInstallationDate(
  plateDate: Date | undefined,
  baseInstallationDate: Date | undefined
) {
  if (!plateDate || plateDate.getTime() === baseInstallationDate?.getTime()) {
    return baseInstallationDate
  }
  return plateDate
}

function comparePolygonalPlatePriority(
  candidate: GeckoPolygonalPlate,
  selected: GeckoPolygonalPlate
) {
  const candidateDate = candidate.installationDate
  const selectedDate = selected.installationDate
  if (candidateDate && selectedDate) {
    const dateDifference = selectedDate.getTime() - candidateDate.getTime()
    if (dateDifference !== 0) return dateDifference
  } else if (candidateDate) {
    return -1
  } else if (selectedDate) {
    return 1
  }

  if (candidate.wallNominalThickness && selected.wallNominalThickness) {
    if (candidate.wallNominalThickness.lt(selected.wallNominalThickness)) {
      return -1
    }
    if (selected.wallNominalThickness.lt(candidate.wallNominalThickness)) {
      return 1
    }
  }
  return 0
}

function getPolygonalPlateAtPoint(
  candidates: PreparedPolygonalPlate[],
  x: number,
  y: number,
  latestDateToUse: Date | undefined
) {
  let selected: GeckoPolygonalPlate | undefined
  for (const candidate of candidates) {
    const { plate } = candidate
    if (
      latestDateToUse &&
      plate.installationDate &&
      !(plate.installationDate <= latestDateToUse)
    ) {
      continue
    }
    if (!booleanPointInPolygon([x, y], candidate.polygon)) continue

    // Center-point matches all have full overlap; retain the shared date/thickness priority and stable ties.
    if (!selected || comparePolygonalPlatePriority(plate, selected) < 0) {
      selected = plate
    }
  }
  return selected
}

/** Prepares indexed, center-point nominal lookup for an eligible basic ship hull. */
export function createBasicShipHullNominalResolver(props: {
  componentId: string
  polygonalPlates: GeckoPolygonalPlate[]
  rectangularPlates: GeckoPlate[]
  baseInstallationDate: Date | undefined
}): BasicShipHullNominalResolver {
  const {
    componentId,
    polygonalPlates,
    rectangularPlates,
    baseInstallationDate,
  } = props
  const preparedPolygons = polygonalPlates.flatMap((plate) => {
    const prepared = preparePolygonalPlate(componentId, plate)
    return prepared ? [prepared] : []
  })
  const preparedRectangles = rectangularPlates.flatMap((plate) => {
    const prepared = prepareRectangularPlate(componentId, plate)
    return prepared ? [prepared] : []
  })
  const polygonIndex = createBucketIndex(preparedPolygons)
  const rectangleIndex = createBucketIndex(preparedRectangles)

  return (binBounds, latestDateToUse) => {
    const { x, y } = getBinCenter(binBounds)
    const polygonalPlate = getPolygonalPlateAtPoint(
      getCandidates(polygonIndex, x, y),
      x,
      y,
      latestDateToUse
    )
    const rectangularPlate = polygonalPlate
      ? undefined
      : getMostRecentPlateInBin(
          getCandidates(rectangleIndex, x, y).map(({ plate }) => plate),
          GeckoComponentRect.CreateFromXYs(
            componentId,
            x,
            y,
            x,
            y,
            METERS,
            METERS
          ),
          latestDateToUse
        )
    const selectedPlate = polygonalPlate ?? rectangularPlate

    return {
      nominalThickness: selectedPlate?.wallNominalThickness,
      installationDate: selectedPlate
        ? resolveInstallationDate(
            selectedPlate.installationDate,
            baseInstallationDate
          )
        : undefined,
    }
  }
}
